import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
export function HeroSection() {
  return <section className="relative pt-12 pb-24 lg:pt-20 lg:pb-32 overflow-hidden bg-cream" data-id="element-170">
      {/* Decorative Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-171">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-peach/40 blur-3xl" data-id="element-172"></div>
        <div className="absolute bottom-[10%] right-[-5%] w-[30%] h-[50%] rounded-full bg-lavender/50 blur-3xl" data-id="element-173"></div>
        <div className="absolute top-[20%] right-[10%] w-[20%] h-[20%] rounded-full bg-mint/40 blur-3xl" data-id="element-174"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-175">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-176">
          {/* Left Column - Content */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-177">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white shadow-sm border border-gray-100 mb-6" data-id="element-178">
              <div className="flex text-accent" data-id="element-179">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-current" data-id="element-180" />)}
              </div>
              <span className="text-xs sm:text-sm font-semibold text-dark" data-id="element-181">
                Trusted by 2,000+ families worldwide
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-extrabold text-dark leading-[1.1] mb-6 text-balance" data-id="element-182">
              Teach Your Child to Understand and Express Their Emotions{' '}
              <span className="text-primary" data-id="element-183">— Through Play</span>
            </h1>

            <p className="text-lg sm:text-xl text-muted mb-8 leading-relaxed text-balance" data-id="element-184">
              The printable game-kit that turns tantrums into laughter and
              frustrations into learning moments. No screens. No drama. Just
              connection.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-8" data-id="element-185">
              <a href="#cta" className="inline-flex justify-center items-center bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:shadow-warm hover:-translate-y-1" data-id="element-186">
                Get the Kit — 50% Off
              </a>
              <a href="#included" className="inline-flex justify-center items-center bg-white hover:bg-gray-50 text-dark border-2 border-gray-200 px-8 py-4 rounded-full font-bold text-lg transition-all hover:-translate-y-1" data-id="element-187">
                See What's Inside ↓
              </a>
            </div>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm font-medium text-muted" data-id="element-188">
              <div className="flex items-center gap-1.5" data-id="element-189">
                <div className="bg-success/10 p-0.5 rounded-full" data-id="element-190">
                  <Check className="w-3.5 h-3.5 text-success" data-id="element-191" />
                </div>
                Instant digital download
              </div>
              <div className="flex items-center gap-1.5" data-id="element-192">
                <div className="bg-success/10 p-0.5 rounded-full" data-id="element-193">
                  <Check className="w-3.5 h-3.5 text-success" data-id="element-194" />
                </div>
                Print at home
              </div>
              <div className="flex items-center gap-1.5" data-id="element-195">
                <div className="bg-success/10 p-0.5 rounded-full" data-id="element-196">
                  <Check className="w-3.5 h-3.5 text-success" data-id="element-197" />
                </div>
                Ages 3-10
              </div>
            </div>
          </motion.div>

          {/* Right Column - Visuals */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative h-[400px] sm:h-[500px] w-full flex items-center justify-center perspective-1000" data-id="element-198">
            {/* Decorative circle behind image */}
            <div className="absolute w-64 h-64 sm:w-80 sm:h-80 bg-accent/20 rounded-full blur-2xl" data-id="element-199"></div>

            <div className="relative z-10 w-full max-w-md mx-auto flex items-center justify-center" data-id="element-200">
              <motion.div animate={{
              y: [-10, 10, -10],
              rotate: [-2, 2, -2]
            }} transition={{
              repeat: Infinity,
              duration: 6,
              ease: 'easeInOut'
            }} className="relative" data-id="element-201">
                <img src="https://cdn.magicpatterns.com/uploads/5SiBdLbjTF6xF5K4EEvxM1/ChatGPT_Image_23_oct_2025,_18_21_20.png" alt="Small Big Emotions Game Kit" className="w-full max-h-[450px] object-contain rounded-3xl shadow-warm-lg border-4 border-white" data-id="element-202" />
                {/* Floating Badge */}
                <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                delay: 0.8,
                type: 'spring'
              }} className="absolute -top-4 -right-4 bg-accent text-dark font-bold px-4 py-2 rounded-full shadow-lg border-2 border-white transform rotate-12" data-id="element-203">
                  50% OFF Today!
                </motion.div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Wave Divider */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none rotate-180" data-id="element-204">
        <svg className="relative block w-full h-[50px] sm:h-[80px]" data-name="Layer 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none" data-id="element-205">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" className="fill-lavender" data-id="element-206"></path>
        </svg>
      </div>
    </section>;
}